// Pan/zoom camera: pointer drag, wheel zoom to cursor, two-finger pinch.

import { WORLD, WORLD_H } from '../world';
// The zoom range, the pan clamp, the wheel rate and the first fit ride
// presentation.toml [presentation.camera]. The range is the one copy:
// audio/model.ts used to mirror MIN_ZOOM by hand.
import {
  CAMERA_FIT_FRACTION, CAMERA_MAX_ZOOM, CAMERA_MIN_ZOOM, CAMERA_PAN_MARGIN,
  CAMERA_WHEEL_ZOOM_RATE,
} from '../gen/presentation';
import { clamp01 } from './math';


/**
 * What the camera writes: a pan offset and one zoom.
 *
 * The camera used to take a scene graph's `Container`, and it read four members of
 * it. The type is structural instead (R431), so the WebGL2 client hands the
 * same class a plain object and the file is shared and not copied. A
 * `Container` satisfies it, so `main.ts` and `arcade/match.ts` are
 * unchanged, and this module now imports no renderer.
 */
export interface PanZoomView {
  x: number;
  y: number;
  scale: { x: number; set(v: number): void };
}

/**
 * What the quake shudder writes: an offset and nothing else.
 *
 * The shake never carries a zoom, so it asks for less than a `PanZoomView`.
 * Both clients pass a wrapper that only moves.
 */
export interface ShakeView {
  x: number;
  y: number;
}

export class Camera {
  /**
   * Every listener this camera put on the canvas, on one signal (R503).
   *
   * The core page builds one camera on a canvas that lives as long as the
   * document, so it never aborts. An arcade page builds one camera per match
   * on ONE canvas the page keeps between matches, and a second camera whose
   * listeners stood beside the first camera's would pan the same view twice
   * for every pointer move. `dispose()` is that page's way out.
   */
  private readonly listeners = new AbortController();
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;
  // Fired after any pan/zoom; drives debounced viewport reports to the
  // server (event-driven — timers break under hidden-tab throttling).
  onChange?: () => void;
  // claim lets a screen-fixed widget (the minimap) take a pointer for
  // itself: asked once at pointerdown, and if it returns a handler that
  // pointer's drags go there instead of panning the world. The event rides
  // along, so a claim can ask what kind of pointer it is: the drag order
  // takes a finger or a pen on an own fleet and never a mouse (R1208).
  claim?: (x: number, y: number, e: PointerEvent) => ((x: number, y: number) => void) | null;
  private claimed = new Map<number, (x: number, y: number) => void>();
  /** Where every live pointer was last seen, whoever owns it. A panning
   *  pointer keeps its own entry current, but a claimed one and a pinching
   *  one do not always, so the release path reads this rather than trusting
   *  the pan entry. */
  private seen = new Map<number, { x: number; y: number }>();
  /**
   * The zoom range the camera allows. It is the game's range, except while
   * photo mode runs past it at both ends (R1624, `ui/photomode.ts`).
   */
  private zoomMin = CAMERA_MIN_ZOOM;
  private zoomMax = CAMERA_MAX_ZOOM;
  /** True while a world smaller than the window stands in its middle and not
   *  at the pan margin. Photo mode alone sets it: a picture at the far zoom
   *  frames the whole world. The game's own clamp is unchanged. */
  private centreSmall = false;
  /**
   * The glide that stands, or null (R1709). `glideTo` starts it and
   * `stepGlide` moves it once a frame. A press or a wheel on the canvas
   * ends it, so a glide never fights the player's own hand.
   */
  private glide: { x0: number; y0: number; x1: number; y1: number; t0: number; ms: number } | null = null;

  /**
   * @param view the world view the camera pans and zooms — its `x`, `y`
   * and `scale` are the camera's only truth. The client passes a plain
   * object; a scene graph's container satisfies the same three members.
   * @param shakeView the view that carries the quake shudder
   * (R101). The offset is deliberately **not** written into `view`: pan is
   * clamped, reported to the server as a viewport and inverted for
   * screen→world picking, and a tremor leaking into any of those would be a
   * bug you would chase for a day. Omit it and the camera never shudders.
   */
  constructor(private view: PanZoomView, canvas: HTMLCanvasElement, private shakeView?: ShakeView) {
    const signal = this.listeners.signal;
    canvas.addEventListener('pointerdown', (e) => {
      // Capture keeps a drag alive outside the window; it can throw for
      // exotic pointer ids, and a failed capture must not kill the drag.
      try { canvas.setPointerCapture(e.pointerId); } catch { /* uncaptured is fine */ }
      this.seen.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.glide = null;
      const handler = this.claim?.(e.clientX, e.clientY, e);
      if (handler) {
        this.claimed.set(e.pointerId, handler);
        handler(e.clientX, e.clientY);
        return;
      }
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2) this.pinchDist = this.currentPinchDist();
    }, { signal });
    canvas.addEventListener('pointermove', (e) => {
      if (this.seen.has(e.pointerId)) {
        this.seen.set(e.pointerId, { x: e.clientX, y: e.clientY });
      }
      const taken = this.claimed.get(e.pointerId);
      if (taken) {
        taken(e.clientX, e.clientY);
        return;
      }
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      if (this.pointers.size === 1) {
        this.view.x += e.clientX - prev.x;
        this.view.y += e.clientY - prev.y;
        this.clamp();
      }
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 2) {
        const dist = this.currentPinchDist();
        if (this.pinchDist > 0) {
          const [a, b] = [...this.pointers.values()];
          this.zoomAt((a.x + b.x) / 2, (a.y + b.y) / 2, dist / this.pinchDist);
        }
        this.pinchDist = dist;
      }
    }, { signal });
    const release = (e: PointerEvent) => {
      this.pointers.delete(e.pointerId);
      this.claimed.delete(e.pointerId);
      this.seen.delete(e.pointerId);
      this.pinchDist = 0;
      // One finger of a pinch has lifted and the survivor pans from here
      // on. Its pan entry must be where that finger is now, or the first
      // move after the lift pans by the whole spread in one frame.
      if (this.pointers.size === 1) {
        for (const id of this.pointers.keys()) {
          const p = this.seen.get(id);
          if (p) this.pointers.set(id, { x: p.x, y: p.y });
        }
      }
    };
    canvas.addEventListener('pointerup', release, { signal });
    canvas.addEventListener('pointercancel', release, { signal });
    // A pointer whose capture is taken away raises no `pointerup` at all —
    // backgrounding a telephone in mid-drag is the case that leaves a
    // phantom finger behind, and a phantom means `size` never returns to 1
    // (pan dead) while one real finger reads as a pinch (a drag zooms).
    // `pointerleave` is deliberately **not** bound: a captured mouse drag
    // must survive leaving the window.
    canvas.addEventListener('lostpointercapture', release, { signal });
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.glide = null;
      this.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * CAMERA_WHEEL_ZOOM_RATE));
    }, { passive: false, signal });

    this.fitWorld();
  }

  /**
   * Centres the view and zooms it to show most of the world.
   *
   * The camera is built at boot, before any socket exists, so it fits the
   * default world rectangle. `main.ts` calls this again when the Hello gave
   * a different grid (R261) — the world it fitted is not the world it got.
   *
   * The fit takes the smaller of the two axis scales, so a wide map fits by
   * its width and a tall one by its height (R395).
   */
  fitWorld() {
    const s = Math.min(
      window.innerWidth / (WORLD * CAMERA_FIT_FRACTION),
      window.innerHeight / (WORLD_H * CAMERA_FIT_FRACTION),
    );
    this.view.scale.set(s);
    this.view.x = window.innerWidth / 2 - (WORLD / 2) * s;
    this.view.y = window.innerHeight / 2 - (WORLD_H / 2) * s;
  }

  /** Centres the view on a world point, optionally setting the zoom. It
   *  ends a glide that stands: the newer move wins. */
  centerOn(x: number, y: number, zoom?: number) {
    this.glide = null;
    if (zoom !== undefined) {
      this.view.scale.set(this.clampZoom(zoom));
    }
    this.place(x, y);
  }

  /**
   * Centres the view on a world rect and zooms it so the rect fills the
   * share `fill` of the window on its tighter axis, inside the zoom range
   * (R2709). VIEW of a lane row of FLEET · ROUTES calls it.
   */
  fitRect(x0: number, y0: number, x1: number, y1: number, fill: number) {
    const w = Math.max(1, x1 - x0), h = Math.max(1, y1 - y0);
    const s = Math.min(window.innerWidth / w, window.innerHeight / h) * fill;
    this.centerOn((x0 + x1) / 2, (y0 + y1) / 2, s);
  }

  /** The world point at the centre of the screen: what SEA VIEW HERE
   *  opens at (R2493). */
  centre(): { x: number; y: number } {
    const s = this.view.scale.x;
    return {
      x: (window.innerWidth / 2 - this.view.x) / s,
      y: (window.innerHeight / 2 - this.view.y) / s,
    };
  }

  /** Centres the view on a world point at the zoom now. */
  private place(x: number, y: number) {
    const s = this.view.scale.x;
    this.view.x = window.innerWidth / 2 - x * s;
    this.view.y = window.innerHeight / 2 - y * s;
    this.clamp();
  }

  /**
   * Starts a smooth pan to a world point at the zoom now (R1709). The pan
   * runs `ms` from `now`, on an ease in and out, and `stepGlide` moves it.
   * A glide of 0 ms is a jump, and so is a glide that starts while a
   * finger pans the map: the move still lands once, and the finger keeps
   * the map from then on.
   */
  glideTo(x: number, y: number, ms: number, now: number) {
    if (ms <= 0 || this.pointers.size > 0) {
      this.centerOn(x, y); // which ends a glide that stands
      return;
    }
    const from = this.centre();
    this.glide = { x0: from.x, y0: from.y, x1: x, y1: y, t0: now, ms };
  }

  /** True while a glide stands. */
  get gliding(): boolean { return this.glide !== null; }

  /** Moves the glide that stands to the time `now`. One call a frame. */
  stepGlide(now: number) {
    const g = this.glide;
    if (!g) return;
    const t = clamp01((now - g.t0) / g.ms);
    const e = t < 0.5 ? 4 * t * t * t : 1 - ((2 - 2 * t) ** 3) / 2;
    if (t >= 1) this.glide = null;
    this.place(g.x0 + (g.x1 - g.x0) * e, g.y0 + (g.y1 - g.y0) * e);
  }

  /** The zoom now: the scale of the view. */
  get zoom(): number { return this.view.scale.x; }

  /**
   * Sets the zoom range, and pulls the zoom inside it about the middle of
   * the window. Photo mode passes its own range and `centreSmall`; leaving
   * it passes the game's range back (R1624).
   */
  setZoomRange(min: number, max: number, centreSmall = false) {
    this.zoomMin = min;
    this.zoomMax = max;
    this.centreSmall = centreSmall;
    this.zoomTo(this.view.scale.x);
  }

  /** The game's own zoom range again, with the game's clamp. */
  resetZoomRange() {
    this.setZoomRange(CAMERA_MIN_ZOOM, CAMERA_MAX_ZOOM, false);
  }

  /** Zooms to `zoom` about the middle of the window, inside the range. */
  zoomTo(zoom: number) {
    const old = this.view.scale.x;
    this.zoomAt(window.innerWidth / 2, window.innerHeight / 2, old > 0 ? zoom / old : 1);
  }

  /**
   * The quake shudder, in screen px (R101). Applied to the wrapper, so it
   * cannot fight a drag in progress, cannot survive into the clamp, and
   * cannot corrupt what the world thinks its pan is. Passing (0, 0) is the
   * settled state and costs two property writes.
   */
  setShake(x: number, y: number) {
    if (!this.shakeView) return;
    this.shakeView.x = x;
    this.shakeView.y = y;
  }

  /**
   * Re-applies the pan clamp. The clamp is written against the window, so a
   * resize or a rotation can leave the view outside it; the caller is the
   * window's own resize listener. It fires `onChange`, which is what reports
   * the new viewport to the server — no timer of its own (§10.3).
   */
  reclamp() { this.clamp(); }

  /**
   * Forgets every pointer the camera thinks is down.
   *
   * A page that goes to the background is never told the fingers lifted:
   * the `pointerup` is swallowed and the entry stays. The camera then
   * counts a finger that is not there, so `pointers.size` never falls back
   * to 1 and pan is dead, while one real finger makes the size 2 and a
   * plain drag zooms. The caller is the `visibilitychange` handler, on
   * hide. It touches no pan, no zoom and no shake: a reset is about who is
   * touching the glass, never about where the camera looks.
   */
  reset() {
    this.pointers.clear();
    this.claimed.clear();
    this.seen.clear();
    this.pinchDist = 0;
  }

  /**
   * Hands a claimed pointer back to the pan and the pinch (R1208).
   *
   * A drag order claims the finger that pressed an own fleet. A second
   * finger voids that drag, and the two fingers are then a pinch: the
   * first one joins the pan pointers at the place it was last seen, so
   * the pinch starts from where both fingers are now.
   */
  unclaim(pointerId: number) {
    if (!this.claimed.delete(pointerId)) return;
    const p = this.seen.get(pointerId);
    if (!p) return;
    this.pointers.set(pointerId, { x: p.x, y: p.y });
    if (this.pointers.size === 2) this.pinchDist = this.currentPinchDist();
  }

  /**
   * Takes every listener off the canvas (R503).
   *
   * A page that keeps its canvas and builds a new camera over it calls this
   * on the old one. The core page never does: its camera lives for the
   * document.
   */
  dispose() {
    this.listeners.abort();
  }

  private currentPinchDist(): number {
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  /** A zoom held inside the range the camera allows now. */
  private clampZoom(zoom: number): number {
    return Math.min(this.zoomMax, Math.max(this.zoomMin, zoom));
  }

  private zoomAt(px: number, py: number, factor: number) {
    const old = this.view.scale.x;
    const next = this.clampZoom(old * factor);
    const f = next / old;
    this.view.x = px - (px - this.view.x) * f;
    this.view.y = py - (py - this.view.y) * f;
    this.view.scale.set(next);
    this.clamp();
  }

  private clamp() {
    const s = this.view.scale.x;
    const w = window.innerWidth, h = window.innerHeight;
    // At most CAMERA_PAN_MARGIN of the viewport shows the void beyond the world.
    const margin = CAMERA_PAN_MARGIN;
    this.view.x = Math.min(w * margin, Math.max(w * (1 - margin) - WORLD * s, this.view.x));
    this.view.y = Math.min(h * margin, Math.max(h * (1 - margin) - WORLD_H * s, this.view.y));
    if (this.centreSmall) {
      if (WORLD * s < w * (1 - 2 * margin)) this.view.x = (w - WORLD * s) / 2;
      if (WORLD_H * s < h * (1 - 2 * margin)) this.view.y = (h - WORLD_H * s) / 2;
    }
    this.onChange?.();
  }
}
